import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    try {
      const submitAddr = 'https://readdy.ai/api/form/d96ajffpg5pqhofcumpg';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (!response.ok || (serverMsg && serverMsg.includes('spam'))) {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试。');
    }
  };

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: siteInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const aboutSchema = {
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.description,
    url: 'https://www.chigua.site/about',
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description={`${siteInfo.name} - ${siteInfo.tagline}。${siteInfo.description}`}
        keywords={`关于吃瓜网站,吃瓜网站简介,吃瓜网站联系方式,吃瓜网站投稿`}
        ogType="website"
        schema={[aboutSchema, faqSchema]}
      />

      <Navbar />

      <main>
        <section className="bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

            <div className="mt-8 text-center max-w-3xl mx-auto">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于 <span className="text-primary-500">{siteInfo.name}</span>
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {siteInfo.description}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              {Object.entries(siteInfo.stats).map(([key, value]) => (
                <div key={key} className="text-center bg-background-50 rounded-lg p-5 border border-secondary-200/70">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{value}</div>
                  <div className="text-xs text-foreground-500 mt-1">
                    {key === 'totalArticles' ? '累计文章' :
                     key === 'dailyVisitors' ? '日均访客' :
                     key === 'registeredUsers' ? '注册用户' :
                     key === 'hotTopics' ? '热门话题' : key}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">品牌故事</h2>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {siteInfo.name}诞生于{siteInfo.foundingYear}年，致力于为全网吃瓜群众提供最新、最全、最有趣的网络热点追踪服务。
              </p>
            </div>

            <div className="max-w-2xl mx-auto relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-secondary-200 md:-translate-x-1/2"></div>
              <div className="space-y-8">
                {siteInfo.timeline.map((item, index) => (
                  <div key={index} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} flex-row`}>
                    <div className="hidden md:flex flex-1"></div>
                    <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 border-4 border-background-50">
                      <div className="w-2 h-2 rounded-full bg-background-50"></div>
                    </div>
                    <div className="flex-1 bg-background-100 rounded-lg p-4 border border-secondary-200/70">
                      <span className="text-xs font-bold text-primary-600 whitespace-nowrap">{item.year}</span>
                      <p className="text-sm text-foreground-700 mt-1 leading-relaxed">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">联系我们</h2>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                有料要爆？有瓜要分享？欢迎通过以下方式联系我们！
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-lg p-6 border border-secondary-200/70">
                <h3 className="text-base font-heading font-bold text-foreground-900 mb-4">联系方式</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <a href={`mailto:${siteInfo.contact.email}`} className="text-sm text-foreground-800 hover:text-primary-600 transition-colors">{siteInfo.contact.email}</a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-lg text-accent-600 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信公众号</p>
                      <p className="text-sm text-foreground-800">{siteInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-weibo-line text-lg text-secondary-600 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微博</p>
                      <p className="text-sm text-foreground-800">{siteInfo.contact.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-foreground-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-foreground-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm text-foreground-800">{siteInfo.contact.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-6 border border-secondary-200/70">
                <h3 className="text-base font-heading font-bold text-foreground-900 mb-4">爆料投稿</h3>
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="space-y-4">
                    <div className="field-website-alt">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">您的昵称 *</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 text-sm border border-secondary-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        placeholder="请输入您的昵称"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">电子邮箱 *</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 text-sm border border-secondary-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1">爆料主题</label>
                      <select
                        id="contact-subject"
                        name="subject"
                        className="w-full px-3 py-2.5 text-sm border border-secondary-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all appearance-none"
                      >
                        <option value="">请选择主题</option>
                        <option value="网红塌房爆料">网红塌房爆料</option>
                        <option value="博主反差投稿">博主反差投稿</option>
                        <option value="热门事件线索">热门事件线索</option>
                        <option value="意见建议">意见建议</option>
                        <option value="商务合作">商务合作</option>
                        <option value="其他">其他</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">详细内容 *</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2.5 text-sm border border-secondary-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                        placeholder="请详细描述您要爆料的内容（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '提交爆料'}
                    </button>
                  </div>

                  {formStatus === 'success' && (
                    <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
                      感谢您的投稿！我们会尽快审核处理。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">常见问题</h2>
              <p className="text-sm text-foreground-500">快速解答您的疑问</p>
            </div>

            <div className="max-w-3xl mx-auto space-y-3">
              {siteInfo.faqs.map((faq, index) => (
                <div key={index} className="bg-background-50 border border-secondary-200/70 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <h4 className="text-sm font-semibold text-foreground-800 pr-4">{faq.question}</h4>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-sm text-foreground-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}